import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, LayoutDashboard, Settings, LogOut, Check, Plus, CircleUserRound } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ROLE_META, useSwitchRole } from "@/components/RoleSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { WOMA_SERVICES, ServiceImg, useTapBounce } from "@/components/ServiceImages";

const ServicesGrid = ({ go }) => {
  const { bouncing, tap } = useTapBounce();
  return (
    <div className="grid grid-cols-3 gap-2 px-1" data-testid="account-services-grid">
      {WOMA_SERVICES.map((s) => (
        <button key={s.key} type="button" onClick={() => tap(s.key, () => go(s.route))}
          className="flex flex-col items-center gap-1 rounded-xl py-2 hover:bg-white/5 active:scale-95 transition-transform"
          data-testid={`account-service-${s.key}`}>
          <ServiceImg service={s} bouncing={bouncing === s.key} className="w-12 h-10" />
          <span className="text-[11px] font-semibold text-white">{s.title}</span>
        </button>
      ))}
    </div>
  );
};

const Row = ({ Icon, label, onClick, testId, accent, right }) => (
  <button type="button" onClick={onClick} className={`w-full flex items-center gap-3 py-3 px-3 rounded-xl hover:bg-white/5 text-left ${accent || "text-white"}`} data-testid={testId}>
    <Icon className="w-5 h-5 shrink-0" strokeWidth={1.8} />
    <span className="flex-1 text-sm font-semibold">{label}</span>
    {right}
  </button>
);

// Liens du compte : profils (4 rôles), portefeuille, crédits, paramètres, déconnexion
export const AccountMenuLinks = ({ user, onUserUpdate, onLogout, onNavigate }) => {
  const navigate = useNavigate();
  const { switchRole, switching } = useSwitchRole(user, onUserUpdate);
  const roles = user?.roles?.length ? user.roles : [user?.role].filter(Boolean);
  const active = user?.active_role || user?.role;
  const go = (path) => { onNavigate?.(); navigate(path); };

  const openRole = async (r) => {
    if (r === active) return go(ROLE_META[r].dashboard);
    if (!roles.includes(r)) return go("/dashboard#ajouter-profil");
    if (await switchRole(r)) onNavigate?.();
  };

  return (
    <div className="space-y-5" data-testid="account-menu-links">
      <div>
        <p className="px-3 mb-1 text-[10px] font-black uppercase tracking-widest text-slate-500">Services WÔMA</p>
        <ServicesGrid go={go} />
      </div>
      <div>
        <p className="px-3 mb-1 text-[10px] font-black uppercase tracking-widest text-slate-500">Mes profils</p>
        {Object.entries(ROLE_META).map(([r, m]) => (
          <Row key={r} Icon={m.icon} label={m.label} onClick={() => openRole(r)} testId={`account-role-${r}`}
            accent={r === active ? "text-[#D4AF37]" : undefined}
            right={r === active ? <Check className="w-4 h-4" /> : roles.includes(r) ? (switching ? null : <span className="text-[10px] text-slate-400">Basculer</span>) : <span className="text-[10px] text-slate-500 flex items-center gap-1"><Plus className="w-3 h-3" />Ajouter</span>} />
        ))}
      </div>
      <div>
        <p className="px-3 mb-1 text-[10px] font-black uppercase tracking-widest text-slate-500">Mon compte</p>
        <Row Icon={LayoutDashboard} label="Tableau de bord" onClick={() => go("/dashboard")} testId="account-dashboard" />
        <Row Icon={Wallet} label="Portefeuille & crédits" onClick={() => go("/credits")} testId="account-wallet" />
        <Row Icon={Settings} label="Paramètres" onClick={() => go("/dashboard#parametres")} testId="account-settings" />
      </div>
      <div className="pt-3 border-t border-white/10">
        <Row Icon={LogOut} label="Déconnexion" onClick={() => { onNavigate?.(); onLogout?.(); }} testId="account-logout" accent="text-red-400" />
      </div>
    </div>
  );
};

// Bouton compte (icône) + panneau latéral — ne déborde jamais sur smartphone
export const AccountMenu = ({ user, onUserUpdate, onLogout, className = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <ThemeToggle className="mr-2" />
      <button type="button" onClick={() => setOpen(true)} aria-label="Mon compte"
        className={`w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-[#003594] to-[#D4AF37] flex items-center justify-center font-black text-white ${className}`}
        data-testid="account-menu-btn">
        {user?.full_name ? user.full_name.charAt(0).toUpperCase() : <CircleUserRound className="w-5 h-5" />}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[85vw] max-w-sm overflow-y-auto bg-[#0B1220] border-white/10 text-white" data-testid="account-menu-sheet">
          <SheetHeader>
            <SheetTitle className="text-white text-left truncate">{user?.full_name || "Mon compte"}</SheetTitle>
            <SheetDescription className="text-slate-400 text-left truncate">{user?.email || user?.phone}</SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <AccountMenuLinks user={user} onUserUpdate={onUserUpdate} onLogout={onLogout} onNavigate={() => setOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
